import { useState, type ReactNode } from "react";

import { useCollections } from "../../api/queries";
import { Button } from "../../ui/Button";
import { TextField } from "../../ui/Field";
import { Select } from "../../ui/Select";
import { SettingsPage, SettingsSection } from "../../ui/SettingsSection";
import { useToast } from "../../ui/toast-context";
import { changedFields, useWorkspaceSettings } from "./workspace";

const NO_DEFAULT = "__none__";

/**
 * Workspace identity. Unlike the switches on the other workspace pages these are
 * text fields, so they are edited then saved — and only the fields that actually
 * changed are sent, because `teams.update` changes exactly what it is given.
 */
export function WorkspaceDetailsScreen(): ReactNode {
  const { team, canUpdate, save, saving } = useWorkspaceSettings();
  const collections = useCollections();
  const toast = useToast();

  const current = {
    name: team?.name ?? "",
    description: team?.description ?? "",
    avatarUrl: team?.avatarUrl ?? "",
    defaultCollectionId: team?.defaultCollectionId ?? NO_DEFAULT,
  };

  const [draft, setDraft] = useState(current);
  const [seeded, setSeeded] = useState(team?.id);
  if (team !== undefined && seeded !== team.id) {
    // The workspace arrives after the first render; seed the form from it once.
    setSeeded(team.id);
    setDraft(current);
  }

  const patch = changedFields(current, draft);
  const dirty = Object.keys(patch).length > 0;

  const submit = (): void => {
    if (!dirty) return;
    const body = {
      ...patch,
      ...(patch.description === undefined ? {} : { description: draft.description }),
      ...(patch.avatarUrl === undefined
        ? {}
        : { avatarUrl: draft.avatarUrl.trim() === "" ? null : draft.avatarUrl.trim() }),
      ...(patch.defaultCollectionId === undefined
        ? {}
        : {
            defaultCollectionId:
              draft.defaultCollectionId === NO_DEFAULT ? null : draft.defaultCollectionId,
          }),
    };
    save(body);
    toast.show("Workspace details saved.");
  };

  const collectionOptions = [
    { value: NO_DEFAULT, label: "No default — land on Home" },
    ...(collections.data ?? []).map((collection) => ({
      value: collection.id,
      label: collection.name,
    })),
  ];

  return (
    <SettingsPage title="Details" description="How this workspace names and presents itself.">
      <SettingsSection title="Identity">
        <div className="flex flex-col gap-4">
          <TextField
            label="Name"
            value={draft.name}
            disabled={!canUpdate}
            onChange={(value) => {
              setDraft({ ...draft, name: value });
            }}
          />
          <TextField
            label="Description"
            description="A sentence about what this workspace is for."
            value={draft.description}
            disabled={!canUpdate}
            onChange={(value) => {
              setDraft({ ...draft, description: value });
            }}
          />
          <TextField
            label="Logo URL"
            description="A square image, at least 256 pixels. Leave empty for initials."
            type="url"
            value={draft.avatarUrl}
            disabled={!canUpdate}
            onChange={(value) => {
              setDraft({ ...draft, avatarUrl: value });
            }}
          />
        </div>
      </SettingsSection>

      <SettingsSection title="Start page" description="Where people land when they sign in.">
        <Select
          label="Default collection"
          value={draft.defaultCollectionId}
          options={collectionOptions}
          disabled={!canUpdate || collections.isPending}
          onChange={(value) => {
            setDraft({ ...draft, defaultCollectionId: value });
          }}
        />
      </SettingsSection>

      {canUpdate && (
        <div className="flex items-center gap-3">
          <Button variant="primary" disabled={!dirty || saving} onClick={submit}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
          {dirty && <span className="text-xs text-muted">Unsaved changes</span>}
        </div>
      )}
      {!canUpdate && (
        <p className="text-sm text-muted">Only a workspace administrator may change these.</p>
      )}
    </SettingsPage>
  );
}
